/** @typedef {{
 *   id: string,
 *   en: string,
 *   fr: string,
 *   author?: string,
 *   reflectionEn?: string,
 *   reflectionFr?: string,
 * }} DailyQuote */

/** Quotes shown in Procsystim — never use "Unknown" as author. */
/** @type {DailyQuote[]} */
export const DAILY_QUOTES = [
  {
    id: "q1",
    en: "Change does not happen overnight. True transformation requires time, patience, and commitment to the process. It is built through the systems we create, the habits we remove, and the habits we develop. Every action repeated today shapes the person we become tomorrow.",
    fr: "Le changement n’arrive pas du jour au lendemain. Une vraie transformation demande du temps, de la patience et un engagement dans le processus. Elle se construit à travers les systèmes que nous créons, les habitudes que nous abandonnons et celles que nous développons. Chaque action répétée aujourd’hui façonne la personne que nous devenons demain.",
  },
  {
    id: "q2",
    en: "You do not rise to the level of your goals. You fall to the level of your systems.",
    fr: "Vous ne vous élevez pas au niveau de vos objectifs. Vous retomberez au niveau de vos systèmes.",
    author: "James Clear, Atomic Habits",
    reflectionEn:
      "Your environment and daily systems shape your results more than motivation alone.",
    reflectionFr:
      "Votre environnement et vos systèmes quotidiens façonnent vos résultats plus que la seule motivation.",
  },
  {
    id: "q3",
    en: "As long as you are taking steps toward changing your life, time is only a matter of when—not if. Every action, every habit you build, and every improvement you make brings you closer to the person you want to become.",
    fr: "Tant que vous avancez pour changer votre vie, le temps n’est qu’une question de quand — pas de si. Chaque action, chaque habitude que vous bâtissez et chaque progrès que vous faites vous rapproche de la personne que vous voulez devenir.",
  },
  {
    id: "q4",
    en: "Don't wait for the right moment, because the right moment doesn't exist. Take small steps, stay consistent, and let your efforts compound over time.",
    fr: "N’attendez pas le bon moment, car le bon moment n’existe pas. Faites de petits pas, restez constant, et laissez vos efforts se composer avec le temps.",
  },
  {
    id: "q5",
    en: "Compound interest grows your money over time. Good habits and bad habits shape your life the same way.",
    fr: "Les intérêts composés font croître votre argent avec le temps. Les bonnes et les mauvaises habitudes façonnent votre vie de la même façon.",
  },
  {
    id: "q6",
    en: "A bucket of water thrown once leaves little mark. A single drop, repeated long enough, can wear through stone. That's the power of consistency.",
    fr: "Un seau d’eau jeté une seule fois laisse peu de trace. Une seule goutte, répétée assez longtemps, peut percer la pierre. Voilà le pouvoir de la constance.",
  },
];

/**
 * @param {DailyQuote} quote
 * @param {'en'|'fr'} locale
 */
export function quoteText(quote, locale) {
  if (!quote) return "";
  if (locale === "fr") return quote.fr || quote.en || quote.text || "";
  return quote.en || quote.text || quote.fr || "";
}

/**
 * @param {DailyQuote | null | undefined} quote
 * @param {'en'|'fr'} locale
 */
export function quoteReflection(quote, locale) {
  if (!quote) return "";
  if (locale === "fr") return quote.reflectionFr || quote.reflectionEn || "";
  return quote.reflectionEn || quote.reflectionFr || "";
}

/**
 * @param {DailyQuote | null | undefined} quote
 */
export function quoteAuthor(quote) {
  if (!quote) return "";
  return typeof quote.author === "string" ? quote.author : "";
}

/**
 * @param {unknown} raw
 * @returns {DailyQuote | null}
 */
export function normalizeStoredQuote(raw) {
  if (!raw || typeof raw !== "object") return null;
  const id = typeof raw.id === "string" ? raw.id : null;
  if (id) {
    const found = DAILY_QUOTES.find((q) => q.id === id);
    if (found) return found;
  }
  const en = typeof raw.en === "string" ? raw.en : typeof raw.text === "string" ? raw.text : "";
  if (!en) return null;
  const fr = typeof raw.fr === "string" ? raw.fr : en;
  /** @type {DailyQuote} */
  const next = { id: id || `legacy_${en.slice(0, 12)}`, en, fr };
  if (typeof raw.author === "string" && raw.author) next.author = raw.author;
  if (typeof raw.reflectionEn === "string" && raw.reflectionEn) next.reflectionEn = raw.reflectionEn;
  if (typeof raw.reflectionFr === "string" && raw.reflectionFr) next.reflectionFr = raw.reflectionFr;
  return next;
}
